import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { LogOut, User, Settings, Menu, X, ChevronDown, Sparkles, DollarSign, BarChart3 } from 'lucide-react'; // Add BarChart3 import

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const isCustomerMenuRoute = location.pathname.includes('/customer/');

  if (isCustomerMenuRoute) {
    return null;
  }

  const navigation = [
    { name: t('navigation.features'), href: '/#features', current: location.hash === '#features' },
    { name: t('navigation.pricing'), href: '/pricing', current: location.pathname === '/pricing' },
    { name: t('navigation.about'), href: '/about', current: location.pathname === '/about' },
  ];

  // Define user menu items based on role
  const getUserMenuItems = () => {
    // If user is restaurant, return empty array (no profile or subscription)
    if (user?.role === 'restaurant') {
      return [];
    }

    // For other roles (staff, admin, etc.)
    const baseItems = [
      { icon: User, label: t('navigation.profile'), href: '/profile', color: 'text-blue-600' },
    ];

    // Only include subscription if user is not staff
    if (user?.role !== 'staff') {
      baseItems.push(
        {
          icon: Settings,
          label: t('navigation.subscription'),
          href: '/subscription',
          color: 'text-purple-600'
        },
        {
          icon: DollarSign,
          label: t('navigation.pricing'),
          href: '/pricing',
          color: 'text-green-600'
        }
      );
    }

    return baseItems;
  };

  const userMenuItems = getUserMenuItems();

  // Logo link destination based on authentication
  const logoLink = isAuthenticated() ? '/dashboard' : '/';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-100'
        : 'bg-white/80 backdrop-blur-md border-b border-gray-100'
        }`}>
        <div className="container">
          <div className="flex justify-between items-center py-3 ">
            {/* Logo - Links to dashboard when authenticated, home when not */}
            <Link
              to={logoLink}
              className="flex items-center space-x-3 group"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="relative">
                <div className="w-14 h-14">
                  <img
                    src="/logo.png"
                    alt="logo"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Mytableflow
                </span>
                <span className="text-xs text-gray-500 -mt-1">
                  {isAuthenticated() ? t('navigation.dashboard') : 'Modern Restaurant Management'}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Only show when not authenticated */}
            {!isAuthenticated() && (
              <div className="hidden md:flex items-center space-x-8 ">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative font-medium transition-all duration-300 hover:text-blue-600 ${item.current ? 'text-blue-600' : 'text-gray-700'
                      }`}
                  >
                    {item.name}
                    {item.current && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"></div>
                    )}
                  </Link>
                ))}
              </div>
            )}

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <LanguageSwitcher variant="minimal" />
              {isAuthenticated() ? (
                <>
                  {/* User Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-50 transition-all duration-300 border border-transparent hover:border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                          {user?.name?.charAt(0) || user?.restaurant_name?.charAt(0) || 'U'}
                        </div>
                        <div className="text-left hidden lg:block">
                          <div className="text-sm font-medium text-gray-900">
                            {user?.name || user?.restaurant_name || 'User'}
                          </div>
                          <div className="flex items-center space-x-2">
                            {user?.role && (
                              <span className="px-2 py-0.5 text-xs bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full font-medium">
                                {user.role}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">Active</span>
                          </div>
                        </div>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''
                        }`} />
                    </button>

                    {/* User Dropdown Menu */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 backdrop-blur-xl">
                        {/* User Info */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-semibold text-gray-900">
                            {user?.name || user?.restaurant_name || 'User'}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {user?.email || 'user@example.com'}
                          </div>
                        </div>

                        {/* Menu Items - Only show if there are items */}
                        {userMenuItems.length > 0 && (
                          <div className="py-2">
                            {userMenuItems.map((item) => {
                              const Icon = item.icon;
                              return (
                                <Link
                                  key={item.label}
                                  to={item.href}
                                  onClick={() => setIsUserMenuOpen(false)}
                                  className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 group"
                                >
                                  <Icon className={`h-4 w-4 ${item.color} group-hover:scale-110 transition-transform`} />
                                  <span>{item.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}

                        {/* Statistics Link - Only show for admin users */}
                        {user?.role !== 'staff' && (
                          <div className="border-t border-gray-100 pt-2">
                            <Link
                              to="/SimpleStatistics"
                              onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 group"
                            >
                              <BarChart3 className="h-4 w-4 text-green-600 group-hover:scale-110 transition-transform" />
                              <span>Statistics</span>
                            </Link>
                          </div>
                        )}

                        {/* Dashboard Link */}
                        <div className="border-t border-gray-100 pt-2">
                          <Link
                            to="/dashboard"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-all duration-300 group"
                          >
                            <Settings className="h-4 w-4 text-gray-600 group-hover:text-blue-600 transition-colors" />
                            <span>{t('navigation.dashboard')}</span>
                          </Link>
                        </div>

                        {/* Logout */}
                        <div className="border-t border-gray-100 pt-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-all duration-300 group"
                          >
                            <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform" />
                            <span>{t('navigation.logout')}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Auth Links - Desktop */}
                  <div className="hidden md:flex items-center space-x-4">
                    <Link
                      to="/login"
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300"
                    >
                      {t('navigation.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>{t('navigation.register')}</span>
                    </Link>
                  </div>

                  {/* Mobile Menu Button */}
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="md:hidden p-2 rounded-xl hover:bg-gray-50 transition-colors duration-300"
                  >
                    {isMobileMenuOpen ? (
                      <X className="h-6 w-6 text-gray-700" />
                    ) : (
                      <Menu className="h-6 w-6 text-gray-700" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu - Only show when not authenticated */}
        {isMobileMenuOpen && !isAuthenticated() && (
          <div className="md:hidden border-t border-gray-200 bg-white/95 backdrop-blur-xl">
            <div className="container py-4 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 font-medium transition-colors duration-300 ${item.current ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                    }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center py-3 text-gray-700 font-medium border-2 border-gray-200 rounded-xl hover:border-blue-600 hover:text-blue-600 transition-all duration-300"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {t('navigation.register')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop for user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}

      {/* Spacer for fixed navbar */}
      <div className="h-16"></div>
    </>
  );
};

export default Navbar;